/* global Rx */
const STRM_CON_TYPE_SELF = 'self'
const STRM_CON_TYPE_IN = 'in'
const STRM_CON_TYPE_OUT = 'out'

const STRM_CHAIN_START = 'STRM_CHAIN_START'
const STRM_CHAIN_MIDDLE = 'STRM_CHAIN_MIDDLE'
const STRM_CHAIN_END = 'STRM_CHAIN_END'

class StreamChain {
  constructor(eventNames, first) {
    if (!(eventNames instanceof Array)) {
      eventNames = [eventNames]
    }
    this.eventNames = eventNames
    this.chain = [{ type: STRM_CHAIN_START, strm: first }]
  }

  to(strm) {
    this.chain.push({ type: STRM_CHAIN_MIDDLE, strm: strm })
    return this
  }

  end() {
    if (this.chain.length < 1) { return null }
    if (this.chain.length < 2) { return this.chain[0].strm }
    // 最後のエントリのタイプを変更
    this.chain[this.chain.length - 1].type = STRM_CHAIN_END
    this.constructChain()
    return this.chain[0].strm
  }

  constructChain() {
    this.eventNames.forEach(eventName => {
      const arr = this.chain.slice()
      let prev
      arr.forEach(e => {
        const type = e.type
        const strm = e.strm
        if (type === STRM_CHAIN_START) {
          // nothing to do
        } else if (type === STRM_CHAIN_MIDDLE) {
          prev.strm.ensureObservable('out', eventName).subscribe({
            next: v => {
              // prev.out -> self.in
              strm.log_(`Stream chain in evt=${eventName}, strm=${strm.name}`)
              strm.ensureObservable('in', eventName).next(v)
            }
          })
          // self.in -> self.out
          strm.connectInOut(eventName)
        } else if (type === STRM_CHAIN_END) {
          prev.strm.ensureObservable('out', eventName).subscribe({
            next: v => {
              // prev.out -> self.in
              strm.log_(`Stream chain in evt=${eventName}, strm=${strm.name}`)
              strm.ensureObservable('in', eventName).next(v)
            }
          })
        }
        prev = e
      })
    })
  }
}

export default class Stream {
  constructor(opts = {}) {
    this.outObservables = {}
    this.inObservables = {}
    this.debug = opts.hasOwnProperty('debug') ? opts.debug : false
    this.name = opts.name || ''
    this.connections = {
      inMap: {},
      outMap: {},
      selfMap: {},
    }
  }

  log_(msg) {
    if (!this.debug) { return }
    console.log(msg)
  }

  ensureObservable(type, eventName) {
    const obMap = this[`${type}Observables`]
    if (!obMap[eventName]) {
      // subscribeされると、直近1つまでの値をemitしてくれる.
      obMap[eventName] = new Rx.ReplaySubject(1)
    }
    return obMap[eventName]
  }

  // send out event from this stream
  send(eventName, obj) {
    this.log_(`Stream send evt=${eventName} from strm=${this.name}`)
    const observable = this.ensureObservable('out', eventName)
    observable.next(obj)
  }

  // capture event that go out from this stream
  onSend(eventName, func) {
    const observable = this.ensureObservable('out', eventName)
    observable.subscribe({
      next: v => {
        this.log_(`Stream onSend evt=${eventName} from strm=${this.name}`)
        func(v)
      }
    })
  }

  // send in event to this stream
  emitRecv(eventName, obj) {
    this.log_(`Stream emitRecv evt=${eventName}, strm=${this.name}`)
    const observable = this.ensureObservable('in', eventName)
    observable.next(obj)
  }

  // capture event that came in to this stream
  recv(eventName, func) {
    const observable = this.ensureObservable('in', eventName)
    observable.subscribe({
      next: v => {
        this.log_(`Stream recv evt=${eventName} to strm=${this.name}`)
        func(v)
      }
    })
  }

  chain(eventNames) {
    return new StreamChain(eventNames, this)
  }

  addConnectionInfo(conType, eventName, otherStrm) {
    if (conType === STRM_CON_TYPE_SELF) {
      this.connections.selfMap[eventName] = 1
    } else if (conType === STRM_CON_TYPE_IN) {
      this.connections.inMap[`${eventName}-from-${otherStrm.name}`] = 1
    } else if (conType === STRM_CON_TYPE_OUT) {
      this.connections.outMap[`${eventName}-to-${otherStrm.name}`] = 1
    }
  }

  isConnected(conType, eventName, otherStrm) {
    if (conType === STRM_CON_TYPE_SELF) {
      return !!this.connections.selfMap[eventName]
    } else if (conType === STRM_CON_TYPE_IN) {
      return !!this.connections.inMap[`${eventName}-from-${otherStrm.name}`]
    } else if (conType === STRM_CON_TYPE_OUT) {
      return !!this.connections.outMap[`${eventName}-to-${otherStrm.name}`]
    } else {
      return false
    }
  }

  connectInOut(eventName) {
    if (this.isConnected(STRM_CON_TYPE_SELF, eventName)) {
      this.log_(`Stream in->out already connected, evt=${eventName}, strm=${this.name}`)
      return
    }
    this.addConnectionInfo(STRM_CON_TYPE_SELF, eventName)
    this.ensureObservable('in', eventName).subscribe({
      next: v => {
        this.log_(`Stream in->out evt=${eventName}, strm=${this.name}`)
        this.ensureObservable('out', eventName).next(v)
      }
    })
  }

  connectOutOut(eventName, toStrm) {
    if (this.isConnected(STRM_CON_TYPE_OUT, eventName, toStrm)) {
      this.log_(`Stream out->out already connected, evt=${eventName}, strm=${this.name}, toStrm=${toStrm.name}`)
      return
    }
    this.addConnectionInfo(STRM_CON_TYPE_OUT, eventName, toStrm)
    this.ensureObservable('out', eventName).subscribe({
      next: v => {
        this.log_(`Stream out->out evt=${eventName}, strm=${this.name}, toStrm=${toStrm.name}`)
        toStrm.ensureObservable('out', eventName).next(v)
      }
    })
  }

  spread(eventNames, streams, opts = {}) {
    if (!(eventNames instanceof Array)) {
      eventNames = [eventNames]
    }
    if (!(streams instanceof Array)) {
      streams = [streams]
    }
    let mapper = opts.mapper
    if (!mapper) { mapper = a => a }
    eventNames.forEach((eventName, i) => {
      // 自分は素通りすることになるが、コメントインしてしまうと、
      // あるイベント名で双方向のやり取りができなくなる.
      // 若干残念だが、呼ぶ側でイベントを発火する側のstreamのout
      // とこいつのoutを接続する.
      // this.connectInOut(eventName)
      const subject = new Rx.Subject().map(mapper)
      this.ensureObservable('out', eventName).subscribe(subject)
      streams.forEach(strm => {
        subject.subscribe({
          next: v => {
            this.log_(`Stream spread evt=${eventName}, from=${this.name}, to=${strm.name}`)
            let outEventName = eventName
            if (opts.outEventNames) {
              outEventName = opts.outEventNames[i]
            }
            strm.ensureObservable('in', outEventName).next(v)
          }
        })
      })
    })
    // spreadしたあとは大抵一旦仕切り直し.
    // とりまthisでも返しておこう.
    return this
  }

  drain(eventNames, streams, opts = {}) {
    if (!(eventNames instanceof Array)) {
      eventNames = [eventNames]
    }
    if (!(streams instanceof Array)) {
      streams = [streams]
    }
    let mapper = opts.mapper
    if (!mapper) { mapper = a => a }
    eventNames.forEach((eventName, i) => {
      streams.forEach(strm => {
        strm.ensureObservable('out', eventName).map(mapper).subscribe({
          next: v => {
            this.log_(`Stream drain evt=${eventName}, from=${strm.name}, to=${this.name}`)
            // 直接outにつなげる. 下記参照.
            let outEventName = eventName
            if (opts.outEventNames) {
              outEventName = opts.outEventNames[i]
            }
            this.ensureObservable('out', outEventName).next(v)
          }
        })
      })
      // 自分は素通りすることになるが、コメントインしてしまうと、
      // あるイベント名で双方向のやり取りができなくなる.
      // this.connectInOut(eventName)
    })
    // drainしたあとは大抵chainしたい
    return this.chain(eventNames)
  }
}
